
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Pencil, Trash2, Upload, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Application {
  id: string;
  title: string;
  content: string | null;
  status: string | null;
  submitted_at: string;
  team_id: string;
  image_url?: string;
}

interface ApplicationCardProps {
  application: Application;
  canEdit: boolean;
  canDelete: boolean;
  onDeleteClick: (id: string) => void;
}

export const ApplicationCard = ({ 
  application, 
  canEdit, 
  canDelete, 
  onDeleteClick 
}: ApplicationCardProps) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(application.title);
  const [content, setContent] = useState(application.content || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Generate subtle variations based on application ID for visual diversity
  const appVariation = application.id.charCodeAt(0) % 6;
  
  const getGradientVariation = () => {
    switch (appVariation) {
      case 0:
        return "from-blue-50/80 to-blue-100/60"; // Soft blue
      case 1:
        return "from-purple-50/80 to-purple-100/60"; // Soft purple
      case 2:
        return "from-emerald-50/80 to-emerald-100/60"; // Soft emerald
      case 3:
        return "from-orange-50/80 to-orange-100/60"; // Soft orange
      case 4:
        return "from-pink-50/80 to-pink-100/60"; // Soft pink
      case 5:
        return "from-teal-50/80 to-teal-100/60"; // Soft teal
      default:
        return "from-blue-50/80 to-blue-100/60";
    }
  };

  const getBorderColor = () => {
    switch (appVariation) {
      case 0:
        return "border-blue-200/40";
      case 1:
        return "border-purple-200/40";
      case 2:
        return "border-emerald-200/40";
      case 3:
        return "border-orange-200/40";
      case 4:
        return "border-pink-200/40";
      case 5:
        return "border-teal-200/40";
      default:
        return "border-blue-200/40";
    }
  };
  
  const handleEditClick = () => {
    setTitle(application.title);
    setContent(application.content || "");
    setImageFile(null);
    setIsEditing(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSubmitting(true);
      
      // Prepare the update data
      const updateData: Partial<Application> = {
        title,
        content
      };
      
      // Upload the image if a new one was selected
      if (imageFile) {
        // Create a unique file path
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `application_images/${fileName}`;
        
        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('applications')
          .upload(filePath, imageFile);
          
        if (uploadError) {
          throw uploadError;
        }
        
        // Get the public URL
        const { data: urlData } = supabase
          .storage
          .from('applications')
          .getPublicUrl(filePath);
          
        updateData.image_url = urlData.publicUrl;
      }
      
      // Update the application in the database
      const { error } = await supabase
        .from('applications')
        .update(updateData)
        .eq('id', application.id);
        
      if (error) throw error;
      
      toast({
        title: "Application updated",
        description: "The application has been updated successfully."
      });
      
      // Refresh the page to see the changes
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Failed to update",
        description: error.message || "An error occurred while updating the application.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      setIsEditing(false);
    }
  };

  return (
    <>
      <Card className={cn(
        "relative overflow-hidden group transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl border h-full flex flex-col",
        `bg-gradient-to-br ${getGradientVariation()}`,
        getBorderColor()
      )}>
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full -translate-y-4 translate-x-4"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 bg-white rounded-full translate-y-3 -translate-x-3"></div>
        </div>
        
        {/* Subtle shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
        
        <div className="relative p-6 flex flex-col h-full">
          {/* Icon now centered above the title */}
          <div className="flex flex-col items-center justify-center mb-4">
            <div className="p-2 bg-white/40 text-slate-600 rounded-full mb-2 backdrop-blur-sm border border-white/60 group-hover:scale-110 transition-transform duration-300">
              <img 
                src="/lovable-uploads/b476de88-d1fb-48c9-8796-68a63cc74f68.png" 
                alt="Application icon" 
                className="w-5 h-5"
              />
            </div>
            
            <h3 className="font-medium text-lg text-center text-slate-700 group-hover:text-slate-600 transition-colors">{application.title}</h3>
          </div>
          
          {/* Edit button moved to top */}
          {canEdit && (
            <div className="flex justify-center -mt-2 mb-3">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="p-0 bg-white/25 hover:bg-white/40 backdrop-blur-sm transition-all duration-300" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditClick();
                      }}
                    >
                      <Pencil className="w-5 h-5 text-slate-600" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit application</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          
          <div className="flex-1 mb-5 flex flex-col items-center">
            <img 
              src={application.image_url || "/lovable-uploads/251380b1-4d53-4124-bf15-25f08257a46b.png"} 
              alt="Application illustration" 
              className="w-32 h-32 object-contain mb-3 rounded-xl bg-white/20 p-2 backdrop-blur-sm border border-white/30"
            />
            <p className="text-sm text-slate-600/80 line-clamp-3 mt-2 text-center">
              {application.content ? "Click view to open the application" : "No content available."}
            </p>
          </div>
          
          <div className="mt-auto flex flex-col gap-3">
            {canDelete && (
              <Button
                variant="destructive"
                className="w-full bg-red-600/90 hover:bg-red-700 backdrop-blur-sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteClick(application.id);
                }}
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
        
        {/* View button positioned at bottom edge-to-edge */}
        <Button 
          className="w-full rounded-none h-12 text-base bg-white/25 border-white/40 text-slate-700 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300 font-medium mt-auto border-t"
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (application.content) {
              // If the content is a valid URL, open it in a new tab
              try {
                const url = new URL(application.content);
                window.open(url.toString(), '_blank', 'noopener,noreferrer');
              } catch (error) {
                // If not a valid URL, just show a toast
                toast({
                  title: "Invalid URL",
                  description: "The application link is not a valid URL.",
                  variant: "destructive"
                });
              }
            } else {
              toast({
                title: "No content",
                description: "This application has no content to view.",
                variant: "destructive"
              });
            }
          }}
        >
          <Eye className="w-5 h-5 mr-2" />
          View
        </Button>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>
              Make changes to your application here. Click save when you're done.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="content">Link URL</Label>
              <Input
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="image">Application Image</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={() => document.getElementById('image')?.click()}
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
              {imageFile && (
                <p className="text-sm text-muted-foreground">
                  New image selected: {imageFile.name}
                </p>
              )}
              {application.image_url && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground mb-2">Current image:</p>
                  <img 
                    src={application.image_url} 
                    alt="Current application image" 
                    className="w-full max-h-32 object-contain border rounded-md"
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveChanges}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
